// prepaidVendorMatch.js — THE ONE prepaid-contract -> vendor IDENTITY resolver.
//
// WHY THIS EXISTS. A prepaid contract has been tied to its vendor BY NAME in a dozen
// places, in five different key spellings, with three different tie-breaks (last-wins,
// first-wins, all-match). A NAME IS NOT AN IDENTITY: AE Model has four duplicate-name
// vendor groups (Misc x9, Regus x2, Robert Half x2, Videoask x2) that are DIFFERENT
// VENDORS SHARING A LABEL, separated by the IS line they sit on. Measured on the real
// operator templates, a name-only key leaves 12 of 170 contracts ambiguous; adding the
// GL line leaves 0. Evidence: diffs/regus-phase0.txt + diffs/regus-remeasure.txt.
//
// PURE, like its sibling prepaidRouting.js: it returns a VERDICT and lets each caller
// act. It NEVER books, NEVER warns, NEVER throws on data — only on a caller that fails
// to declare the line vocabulary (see below), which is a code error, not a data error.
//
// ★★ THE LINE VOCABULARY IS THE CALLER'S DECLARATION, NOT A DEFAULT.
// The two sides of the join must be compared in the SAME vocabulary, and there are two:
//   · PARSE TIME (the wizard, before any vendor row exists in the DB):
//        vendor line  = the vendor sheet's `gl_line_name`   ("Office Rent")
//        contract line= the prepaid sheet's `gl_line_name`, which parseVendorExcel:558
//                       maps into `contract.account`        ("Office Rent")
//   · RUNTIME (the live model, where vendors carry a resolved slug):
//        vendor line  = `vendors.is_line`                   ("ga_office_rent")
//        contract line= resolvePrepaidContractLine(c).lineKey
// A LABEL compared against a SLUG matches nothing and would silently un-link every
// contract, so `lineOf` is REQUIRED on both calls and a missing one THROWS by name.
// Defaulting it is the one mistake that would look like it works.

/* THE name key, single-sourced. trim + lowercase — the original buildStep6Vendors
   spelling, adopted as THE spelling (the other sites use raw exact, which is strictly
   narrower; Build 2 moves them onto this one). */
export function prepaidNameKey(s) {
  return String(s == null ? '' : s).trim().toLowerCase();
}

/* The line key: same normalization as the name key, so "Office Rent" and "office rent"
   are the same line and a stray trailing space in a workbook cell cannot orphan a
   contract. Empty string means "this row carries no line information" (a LEGACY vendor
   sheet has no gl_line_name column at all) — callers decide what that means for them;
   this module simply compares it like any other value. */
export function prepaidLineKey(s) {
  return String(s == null ? '' : s).trim().toLowerCase();
}

/* The composite-key separator. U+0000 cannot occur in a trimmed workbook cell, so
   "regus office" + "rent" can never collide with "regus" + "office rent" — a real hazard
   with a space or any printable delimiter.
   ⛔ WRITTEN AS THE ESCAPE, NEVER AS A LITERAL NUL BYTE. A literal one makes git classify
   this file as BINARY: the diff becomes unreviewable and the change ships unread. Caught
   at staging time on the first commit of this module. */
const SEP = '\u0000';

function requireLineOf(lineOf, who) {
  if (typeof lineOf !== 'function') {
    throw new Error(
      `${who}: opts.lineOf is required. The prepaid vendor match compares a contract line ` +
      `against a vendor line and the two must be in the SAME vocabulary — at parse time ` +
      `both are the workbook gl_line_name (contract side arrives as c.account); at runtime ` +
      `both are the IS-leaf slug (vendors.is_line vs resolvePrepaidContractLine(c).lineKey). ` +
      `There is no safe default: a label compared against a slug matches nothing and would ` +
      `silently unlink every contract.`);
  }
}

/* Build the vendor-side index ONCE per resolution pass.
     vendors : the vendor set, in whatever shape the caller holds
     opts.lineOf  (REQUIRED) v => the vendor row line, in the caller's vocabulary
     opts.nameOf  v => the vendor name        (default: v.name ?? v.vendor_name)
     opts.idOf    v => the stable id or null  (default: v.id ?? null)
   Returns { entries, byId, byName, byPair } where an entry is
     { vendor, idx, id, nameKey, lineKey }.
   byName / byPair map to ARRAYS — a collision is data this index must be able to
   REPORT, not a key it is allowed to overwrite. That is the whole bug. */
export function buildPrepaidVendorIndex(vendors, opts) {
  const o = opts || {};
  requireLineOf(o.lineOf, 'buildPrepaidVendorIndex');
  const nameOf = o.nameOf || (v => (v && v.name != null ? v.name : v && v.vendor_name));
  const idOf = o.idOf || (v => (v && v.id != null ? v.id : null));

  const entries = [];
  const byId = new Map();
  const byName = new Map();
  const byPair = new Map();

  (vendors || []).forEach((vendor, idx) => {
    const nameKey = prepaidNameKey(nameOf(vendor));
    const lineKey = prepaidLineKey(o.lineOf(vendor));
    const id = idOf(vendor);
    const entry = { vendor, idx, id, nameKey, lineKey };
    entries.push(entry);
    if (id != null && !byId.has(id)) byId.set(id, entry);   // first wins; ids are unique by construction
    if (!nameKey) return;                                    // an unnamed vendor row joins nothing
    if (!byName.has(nameKey)) byName.set(nameKey, []);
    byName.get(nameKey).push(entry);
    const pk = nameKey + SEP + lineKey;
    if (!byPair.has(pk)) byPair.set(pk, []);
    byPair.get(pk).push(entry);
  });

  return { entries, byId, byName, byPair };
}

/* Resolve ONE contract to ONE vendor.
     contract : the contract row, in whatever shape the caller holds
     index    : from buildPrepaidVendorIndex, built with the SAME vocabulary
     opts.lineOf  (REQUIRED) c => the contract line, in that vocabulary
     opts.nameOf  c => the contract vendor name (default: c.vendor_name ?? c.vendor)
     opts.idOf    c => the stable vendor id     (default: c.vendor_id ?? c.vendorId)

   Returns { vendor, entry, tier, reason, candidates }:
     tier 'id'   reason 'id'         — TIER 1, the stable vendor_id FK resolved.
     tier 'pair' reason 'name+line'  — TIER 2, exactly one vendor on (name, line).
     tier null   reason 'name-on-different-line' — the name matches >=1 vendor, none of
                 them on this line. Bucket B. THE FLAG CASE: on the corrected templates
                 this is empty, and the two rows that used to be here were a template
                 typo (Docusign) the operator has since fixed.
     tier null   reason 'ambiguous'  — >1 vendor shares BOTH name and line. Bucket D.
                 Also the flag case: nothing can tell them apart, so nothing should try.
     tier null   reason 'orphan'     — no vendor of that name at all. Bucket C: the
                 prepaid-only rows (AE Model has 4, all `_archive_*`). ALREADY a named
                 handled state — the wizard 6e "Not in Expenses" badge and the import
                 writing vendor_id NULL. Callers must NOT newly flag this.
   `candidates` carries the same-name entries on the two flag verdicts so a caller can
   NAME the conflict without re-deriving it (prepaidRouting.js returns `ids` the same
   way, for the same reason). */
export function resolvePrepaidVendor(contract, index, opts) {
  const o = opts || {};
  requireLineOf(o.lineOf, 'resolvePrepaidVendor');
  const nameOf = o.nameOf || (c => (c && c.vendor_name != null ? c.vendor_name : c && c.vendor));
  const idOf = o.idOf || (c => {
    if (!c) return null;
    if (c.vendor_id != null) return c.vendor_id;
    if (c.vendorId != null) return c.vendorId;
    return null;
  });
  const idx = index || { byId: new Map(), byName: new Map(), byPair: new Map() };

  // ── TIER 1 — the stable id ────────────────────────────────────────────────────────
  // A miss FALLS THROUGH rather than failing: a stale id on a row whose vendor was
  // replaced should still resolve by name+line. This mirrors the section-12b precedence
  // in runFullModel: `(c.vendor_id && vById.get(...)) || ... || vByName.get(...)`.
  const id = idOf(contract);
  if (id != null) {
    const hit = idx.byId.get(id);
    if (hit) return { vendor: hit.vendor, entry: hit, tier: 'id', reason: 'id', candidates: [hit] };
  }

  const nameKey = prepaidNameKey(nameOf(contract));
  const sameName = idx.byName.get(nameKey) || [];
  if (!nameKey || sameName.length === 0) {
    return { vendor: null, entry: null, tier: null, reason: 'orphan', candidates: [] };
  }

  // ── TIER 2 — name + GL line ───────────────────────────────────────────────────────
  const lineKey = prepaidLineKey(o.lineOf(contract));
  const pair = idx.byPair.get(nameKey + SEP + lineKey) || [];
  if (pair.length === 1) {
    return { vendor: pair[0].vendor, entry: pair[0], tier: 'pair', reason: 'name+line', candidates: pair };
  }
  if (pair.length > 1) {
    return { vendor: null, entry: null, tier: null, reason: 'ambiguous', candidates: pair };
  }
  return { vendor: null, entry: null, tier: null, reason: 'name-on-different-line', candidates: sameName };
}
